import * as THREE from 'three';
import { generateId } from '../utils/helpers';

import { CurtainWallElement } from '../core/types';
import { BaseTool } from './BaseTool';

export interface CurtainWallProperties {
    height: number;
    baseOffset: number;
    topOffset: number;
    baseLevelId: string;
    topLevelId: string;
    gridLayout: 'None' | 'Fixed Distance' | 'Fixed Number' | 'Maximum Spacing';
    verticalGridSpacing?: number;
    horizontalGridSpacing?: number;
}

/**
 * Interactive placement tool for curtain walls: two picks define the wall
 * baseline; the element is added to the BIM store and rendered by
 * CurtainWallSolver through the 'Curtain Walls' renderer case.
 */
export class CurtainWallPlacementTool extends BaseTool {
    id = 'curtain_wall';
    name = 'Curtain Wall';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;

    onPointerDown(event: any): void {
        if (!this.context || event.button !== 0) return;
        const point = event.point as THREE.Vector3 | undefined;
        if (!point) return;

        if (!this.startPoint) {
            this.startPoint = point.clone();
        } else {
            this.placeWall(this.startPoint, point.clone());
            this.startPoint = null;
        }
    }

    onPointerMove(): void { }
    onPointerUp(): void { }

    private placeWall(start: THREE.Vector3, end: THREE.Vector3) {
        const element = createCurtainWall(start, end, {
            height: 3.5,
            baseOffset: 0,
            topOffset: 0,
            baseLevelId: '',
            topLevelId: '',
            gridLayout: 'Fixed Distance',
            verticalGridSpacing: 1.5,
        });
        this.context!.bimStore.addElement(element);
    }
}

export const createCurtainWall = (
    startPoint: THREE.Vector3,
    endPoint: THREE.Vector3,
    properties: CurtainWallProperties
): CurtainWallElement => {
    // Grid generation: vertical grids run along the wall length, horizontal
    // grids up the wall height. Both honour the selected layout mode.
    const length = startPoint.distanceTo(endPoint);
    const height = properties.height;

    const verticalGrids = generateGridLines(length, properties.gridLayout, properties.verticalGridSpacing);
    const horizontalGrids = generateGridLines(height, properties.gridLayout, properties.horizontalGridSpacing);

    return {
        id: generateId(),
        category: 'Curtain Walls',
        type: 'Curtain Wall',
        parameters: {
            ...properties
        },
        constraints: [],
        geometryData: {
            start: [startPoint.x, startPoint.y, startPoint.z],
            end: [endPoint.x, endPoint.y, endPoint.z],
            height: height,
            verticalGrids: verticalGrids,
            horizontalGrids: horizontalGrids
        }
    };
};

/** Interior grid line offsets (from the start edge) for one direction. */
function generateGridLines(
    extent: number,
    layout: CurtainWallProperties['gridLayout'],
    spacing?: number
): number[] {
    if (layout === 'None' || extent <= 0) return [];

    if (layout === 'Fixed Number') {
        // Even divisions; spacing param (when set) is the segment count.
        const segments = Math.max(1, Math.round(spacing ?? 1));
        return Array.from({ length: segments - 1 }, (_, i) => (extent * (i + 1)) / segments);
    }

    const step = spacing && spacing > 0 ? spacing : 1.5;
    if (layout === 'Maximum Spacing') {
        // Guarantee no segment exceeds the maximum: even divisions sized so
        // every span stays within the spacing.
        const segments = Math.max(1, Math.ceil(extent / step));
        return Array.from({ length: segments - 1 }, (_, i) => (extent * (i + 1)) / segments);
    }

    // Fixed Distance: interior lines at exact multiples of the spacing.
    const lines: number[] = [];
    for (let offset = step; offset < extent - 1e-6; offset += step) {
        lines.push(offset);
    }
    return lines;
}
